import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { TrendingUp, Search, Clock, ArrowRight } from 'lucide-react';
import { useTenantAuth } from '../contexts/TenantAuthContext';

/** Converts an epoch ms timestamp to a human-readable "X mins ago" string. */
function timeAgo(updatedAt: number | undefined): string {
  if (!updatedAt) return 'Updated recently';
  const mins = Math.round((Date.now() - updatedAt) / 60_000);
  if (mins <= 0) return 'Updated just now';
  if (mins === 1) return 'Updated 1 min ago';
  if (mins < 60) return `Updated ${mins} mins ago`;
  const hrs = Math.round(mins / 60);
  if (hrs === 1) return 'Updated 1 hr ago';
  return `Updated ${hrs} hrs ago`;
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const { tenant } = useTenantAuth();
  const queryClient = useQueryClient();

  // Derive relative timestamps from the React Query cache — zero extra fetches.
  const [engagementUpdated, setEngagementUpdated] = useState('');
  const [aeoUpdated, setAeoUpdated] = useState('');

  useEffect(() => {
    const refresh = () => {
      setEngagementUpdated(
        timeAgo(queryClient.getQueryState(['dashboard-analytics'])?.dataUpdatedAt)
      );
      setAeoUpdated(
        timeAgo(queryClient.getQueryState(['ai-authority'])?.dataUpdatedAt)
      );
    };

    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [queryClient]);

  return (
    <div className="space-y-10">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {tenant?.name ? `${tenant.name} Dashboard` : 'Dashboard'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Select a dashboard below to view detailed analytics.
          </p>
        </div>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Engagement */}
        <div data-feature-id='engagement-dashboard' className="bg-white shadow-sm overflow-hidden flex flex-col rounded-md">
          <div className="p-6 flex-1 mb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg border border-indigo-200 bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <TrendingUp className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Engagement Dashboard</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Monitor how users interact with your widget
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex-shrink-0">
                Active
              </span>
            </div>
          </div>
          {/* Action bar */}
          <button
            onClick={() => navigate('/dashboard/engagement')}
            className="w-full bg-indigo-700 hover:bg-indigo-800 active:bg-indigo-900 transition-colors px-6 py-4 flex items-center justify-between"
          >
            <span className="flex items-center gap-1.5 text-white text-sm">
              <Clock className="h-3.5 w-3.5" />
              {engagementUpdated || 'Updated recently'}
            </span>
            <span className="flex items-center gap-1 text-white text-sm font-medium">
              View analytics
              <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        </div>

        {/* AEO */}
        <div className="bg-white shadow-sm overflow-hidden flex flex-col rounded-md">
          <div className="p-6 flex-1 mb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg border border-purple-200 bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Search className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">AEO Dashboard</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Track your performance on AI models
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex-shrink-0">
                Active
              </span>
            </div>
          </div>
          {/* Action bar */}
          <button
            onClick={() => navigate('/dashboard/aeo')}
            className="w-full bg-purple-700 hover:bg-purple-800 active:bg-purple-900 transition-colors px-6 py-4 flex items-center justify-between"
          >
            <span className="flex items-center gap-1.5 text-white text-sm">
              <Clock className="h-3.5 w-3.5" />
              {aeoUpdated || 'Updated recently'}
            </span>
            <span className="flex items-center gap-1 text-white text-sm font-medium">
              View analytics
              <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        </div>
      </div>

      {/* System status */}
      <div className='p-6 bg-white'>
        <p className="text-sm font-semibold text-gray-500 capitalize tracking-wide mb-4">
          System status
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { name: 'AI Assistant', status: 'Online and responding' },
            { name: 'Documents', status: 'Up to date' },
            { name: 'Analytics', status: 'Tracking active' },
          ].map((item) => (
            <div
              key={item.name}
              className="flex items-center gap-3 p-4 bg-gray-100 border border-gray-100"
            >
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-gray-900">{item.name}</div>
                <div className="text-xs text-gray-500">{item.status}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
